import type { GovernancePolicyFormData } from '@experiences/interfaces';
import type { WizardStepName } from '@experiences/telemetry';
import {
    GovernanceEvent,
    portalTelemetry,
} from '@experiences/telemetry';
import type { MutableRefObject } from 'react';
import type React from 'react';
import {
    useEffect,
    useRef,
} from 'react';
import { useFormContext } from 'react-hook-form';

import { useUiStepper } from '../../../common/UiWizard/UiStepperContextProvider';

const STEP_NAMES: readonly WizardStepName[] = [
    'Scope',
    'PolicyDefinition',
    'NameAndDescription',
    'EnforcementMode',
    'ReviewAndSave',
] as const;

interface GovernanceWizardTelemetryTrackerProps {
    mode: 'create' | 'edit';
    /** Set true only after the submit handler resolves successfully — confirms a real save. */
    savedRef: MutableRefObject<boolean>;
    /** True while the submit handler is in flight — suppresses Abandoned during the success-path unmount. */
    submittingRef: MutableRefObject<boolean>;
}

/**
 * Renders nothing — emits Governance.PolicyWizard* funnel telemetry by subscribing to the
 * wizard stepper state. Must be rendered inside `UiStepperProvider` and the wizard form context.
 */
export const GovernanceWizardTelemetryTracker: React.FC<GovernanceWizardTelemetryTrackerProps> = ({
    mode,
    savedRef,
    submittingRef,
}) => {
    const { currentStep } = useUiStepper();
    const { getValues } = useFormContext<GovernancePolicyFormData>();

    const currentDate = Date.now();
    const wizardStartRef = useRef<number>(currentDate);
    const stepEnterRef = useRef<number>(currentDate);
    const previousStepRef = useRef<number>(currentStep);
    const lastCompletedStepRef = useRef<WizardStepName | 'None'>('None');
    const initialFiredRef = useRef(false);

    // First-mount events: Started (create mode only) + initial StepViewed.
    useEffect(() => {
        if (initialFiredRef.current) {
            return;
        }
        initialFiredRef.current = true;

        if (mode === 'create') {
            portalTelemetry.trackEvent({
                name: GovernanceEvent.PolicyWizardStarted,
                properties: { EntryPoint: 'AddPolicyButton' },
            });
        }

        const initialName = STEP_NAMES[currentStep];
        if (initialName) {
            portalTelemetry.trackEvent({
                name: GovernanceEvent.PolicyWizardStepViewed,
                properties: {
                    StepName: initialName,
                    TimeSpentPreviousStep: 0,
                },
            });
        }
        stepEnterRef.current = Date.now();
        previousStepRef.current = currentStep;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Step-transition events.
    useEffect(() => {
        if (!initialFiredRef.current || currentStep === previousStepRef.current) {
            return;
        }
        const now = Date.now();
        const elapsed = now - stepEnterRef.current;
        const prevName = STEP_NAMES[previousStepRef.current];
        const newName = STEP_NAMES[currentStep];

        // Forward navigation counts as "completing" the prior step.
        if (currentStep > previousStepRef.current && prevName) {
            portalTelemetry.trackEvent({
                name: GovernanceEvent.PolicyWizardStepCompleted,
                properties: {
                    StepName: prevName,
                    StepCompletionDurationMs: elapsed,
                },
            });
            lastCompletedStepRef.current = prevName;
        }

        if (newName) {
            portalTelemetry.trackEvent({
                name: GovernanceEvent.PolicyWizardStepViewed,
                properties: {
                    StepName: newName,
                    TimeSpentPreviousStep: elapsed,
                },
            });
        }

        stepEnterRef.current = now;
        previousStepRef.current = currentStep;
    }, [ currentStep ]);

    // Abandoned event — fires on unmount unless the wizard either saved or is mid-submit.
    useEffect(() => () => {
        if (savedRef.current || submittingRef.current) {
            return;
        }
        const values = getValues();
        const hasResources = (values.selectors?.length ?? 0) > 0 ? 'Yes' : 'No';
        const hasActors =
            (values.actorRule?.values?.length ?? 0) > 0
            || (values.executableRule?.values?.length ?? 0) > 0
                ? 'Yes'
                : 'No';

        portalTelemetry.trackEvent({
            name: GovernanceEvent.PolicyWizardAbandoned,
            properties: {
                LastCompletedStep: lastCompletedStepRef.current,
                TimeSpentInWizard: Date.now() - wizardStartRef.current,
                HasConfiguredResources: hasResources,
                HasConfiguredActors: hasActors,
            },
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return null;
};
